/* © ИП Коробкин И.Н., KIN DIGITAL CRM+ */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Upload } from 'lucide-react';
import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { PARTY_KIND_LABEL } from '@kd/shared';
import { Badge, Button, Empty, ErrorText, Field, Input, Modal, PageHeader, Pager, Select, Spinner } from '@/components/ui';
import { PartyForm } from '@/components/PartyForm';
import { api, qs } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { useDebounced } from '@/lib/hooks';

export function PartiesPage() {
  const { can } = useAuth();
  const nav = useNavigate();
  const qc = useQueryClient();
  const [q, setQ] = useState('');
  const [role, setRole] = useState('');
  const [kind, setKind] = useState('');
  const [page, setPage] = useState(1);
  const [creating, setCreating] = useState(false);
  const [importing, setImporting] = useState(false);
  const dq = useDebounced(q);
  const { data, isLoading } = useQuery({ queryKey: ['parties', dq, role, kind, page], queryFn: () => api.get(`/parties${qs({ q: dq, role, kind, page, pageSize: 50 })}`) });

  return (
    <>
      <PageHeader title="Клиенты и контрагенты" actions={<>
        {can('party.import') && <Button variant="secondary" onClick={() => setImporting(true)}><Upload size={16} /> Импорт из Excel</Button>}
        {can('party.write') && <Button onClick={() => setCreating(true)}>Новая карточка</Button>}
      </>} />
      <div className="card">
        <div className="flex flex-wrap gap-2 border-b border-slate-200 p-3">
          <Input className="max-w-xs" placeholder="Название, ИНН, телефон, почта" value={q} onChange={(e) => { setQ(e.target.value); setPage(1); }} />
          <Select className="max-w-[180px]" value={role} onChange={(e) => setRole(e.target.value)} placeholder="Все"
            options={[{ value: 'client', label: 'Клиенты' }, { value: 'counterparty', label: 'Контрагенты' }]} />
          <Select className="max-w-[160px]" value={kind} onChange={(e) => setKind(e.target.value)} placeholder="Любой вид"
            options={Object.entries(PARTY_KIND_LABEL).map(([value, label]) => ({ value, label }))} />
        </div>
        {isLoading ? <Spinner /> : !data?.items.length ? <Empty>Ничего не найдено</Empty> : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50"><tr><th className="th">Наименование</th><th className="th">ИНН</th><th className="th">Телефон</th><th className="th">Роль</th><th className="th">Теги</th></tr></thead>
              <tbody>
                {data.items.map((p: any) => (
                  <tr key={p.id} className="border-t border-slate-100 hover:bg-slate-50">
                    <td className="td"><Link to={`/parties/${p.id}`} className="font-medium text-brand-700 hover:underline">{p.name}</Link>
                      <div className="text-xs text-slate-500">{PARTY_KIND_LABEL[p.kind]}</div></td>
                    <td className="td tabular-nums">{p.inn ?? '-'}</td>
                    <td className="td tabular-nums">{p.phones[0] ?? '-'}</td>
                    <td className="td space-x-1">{p.isClient && <Badge tone="brand">клиент</Badge>}{p.isCounterparty && <Badge>контрагент</Badge>}</td>
                    <td className="td space-x-1">{p.tags.map((t: string) => <Badge key={t} tone="blue">{t}</Badge>)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {data && <Pager page={page} pageSize={50} total={data.total} onPage={setPage} />}
      </div>
      <Modal title="Новая карточка" open={creating} onClose={() => setCreating(false)} wide>
        <PartyForm onSaved={(p) => { setCreating(false); qc.invalidateQueries({ queryKey: ['parties'] }); nav(`/parties/${p.id}`); }} />
      </Modal>
      <ImportModal open={importing} onClose={() => setImporting(false)} />
    </>
  );
}

function ImportModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const qc = useQueryClient();
  const [csv, setCsv] = useState('');
  const [fileName, setFileName] = useState('');
  const [target, setTarget] = useState<'client' | 'counterparty'>('client');
  const [tag, setTag] = useState('');
  const m = useMutation({
    mutationFn: () => api.post('/parties/import', { csv, isClient: target === 'client', isCounterparty: target === 'counterparty', tag: tag || undefined }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['parties'] }),
  });
  const onFile = async (f?: File) => {
    if (!f) return;
    setFileName(f.name);
    const buf = await f.arrayBuffer();
    let text = new TextDecoder('utf-8').decode(buf);
    if (text.includes('�')) text = new TextDecoder('windows-1251').decode(buf);
    setCsv(text);
  };
  return (
    <Modal title="Импорт карточек из CSV" open={open} onClose={() => { onClose(); m.reset(); setCsv(''); setFileName(''); }}>
      <div className="space-y-3 text-sm">
        <p className="text-slate-600">В Excel сохраните таблицу как «CSV (разделители - точка с запятой)». Колонки узнаются по названию: Наименование, ИНН, ОГРН, Адрес, Контактное лицо, Телефон, Email, Теги.</p>
        <input type="file" accept=".csv,text/csv" onChange={(e) => onFile(e.target.files?.[0])} />
        {fileName && <div className="text-xs text-slate-500">{fileName}: {csv.split('\n').length - 1} строк</div>}
        <div className="grid grid-cols-2 gap-3">
          <Field label="Кто это"><Select value={target} onChange={(e) => setTarget(e.target.value as any)} options={[{ value: 'client', label: 'Клиенты' }, { value: 'counterparty', label: 'Контрагенты' }]} /></Field>
          <Field label="Тег для всех строк"><Input value={tag} onChange={(e) => setTag(e.target.value)} placeholder="например, база-2026" /></Field>
        </div>
        <ErrorText error={m.error} />
        {m.data && (
          <div className="rounded-md bg-slate-50 p-3">
            <div>Создано: <b>{m.data.created}</b>, пропущено дублей по ИНН: <b>{m.data.skippedDuplicates}</b>, ошибок: <b>{m.data.errors.length}</b></div>
            {m.data.errors.slice(0, 10).map((e: any) => <div key={e.row} className="text-xs text-red-600">Строка {e.row}: {e.message}</div>)}
          </div>
        )}
        <div className="flex justify-end"><Button disabled={!csv || m.isPending} onClick={() => m.mutate()}>{m.isPending ? 'Загрузка...' : 'Импортировать'}</Button></div>
      </div>
    </Modal>
  );
}
